<template>
  <Modal
    ref="modalRef"
    title=""
    type="info"
    :show-icon="true"
    :click-to-close="false"
    with-closer
    negative-action-text="Vazgeç"
    positive-action-text="Onayla"
    :is-positive-action-button-disabled="isJoining || isJoiningByPromoCode"
    :modal-style="{ width: '460px', maxHeight: '80%' }"
    @on-negative-action-click="onJoinDismissClick"
    @on-positive-action-click="onJoinCampaign"
    @modal-closed="onJoinCloseClick"
  >
    <template #body>
      <div
        v-if="campaign"
        class="join-modal"
      >
        <h3 class="join-modal-title">
          Kampanyaya katılımınızı<br>
          onaylıyor musunuz?
        </h3>
        <p class="join-modal-subtitle">
          Geçerli kategorileri, kampanya süresini ve limit bilgilerini <br>
          kontrol ederek katılımınızı onaylayın.
        </p>

        <div class="join-modal-card">
          <div class="join-modal-card-title">
            {{ campaign.name }}
          </div>
          <div
            v-if="campaign.commissionDiscountRate"
            class="join-modal-card-badge"
          >
            %{{ campaign.commissionDiscountRate }} Komisyon İndirimi
          </div>

          <div class="join-modal-card-section">
            <div class="join-modal-card-section-item">
              <span class="label">Geçerlilik Süresi:</span>
              <span class="value">{{ campaign.validityDayCount }} Gün</span>
            </div>
            <div
              v-if="campaign.defaultGmvLimit"
              class="join-modal-card-section-item"
            >
              <span class="label">Kampanya Toplam Satış Limiti (₺):</span>
              <span class="value">{{
                formatNumberBasic(campaign.defaultGmvLimit)
              }}</span>
            </div>
            <div
              v-if="campaign.defaultOrderLimit"
              class="join-modal-card-section-item"
            >
              <span class="label">Kampanya Sipariş Limiti (Adet):</span>
              <span class="value">{{
                formatNumberBasic(campaign.defaultOrderLimit)
              }}</span>
            </div>
          </div>

          <div
            v-if="campaign?.includedCategories?.length > 0"
            class="join-modal-card-section-item"
          >
            <span class="label">Geçerli Kategoriler:</span>
            <span class="value">{{
              campaign?.allCategoryIncluded
                ? 'Tüm Kategoriler'
                : categoriesMapping(campaign?.includedCategories)
            }}</span>
          </div>

          <div
            v-if="campaign?.excludedCategories?.length > 0"
            class="join-modal-card-section-item"
          >
            <span class="label">Geçerli Olmayan Kategoriler:</span>
            <span class="value">{{
              categoriesMapping(campaign?.excludedCategories)
            }}</span>
          </div>
        </div>

        <div class="join-modal-info">
          <StatusIcon
            type="info"
            width="24px"
            height="24px"
          />
          <div class="join-modal-info-text">
            Geçerlilik süresi, toplam satış veya sipariş adedi limitlerinden
            herhangi birine ulaşıldığında kampanya sona erecektir.
          </div>
        </div>
      </div>
    </template>
  </Modal>

  <ModalJoinCampaignResultError ref="joinCampaignResultErrorModalRef" />
</template>

<script setup>
import { ref, defineExpose } from 'vue';
import { useStore } from 'vuex';
import { Modal, StatusIcon } from '@n11/so-ui-components';
import { formatNumberBasic } from '@/utils/formatNumber';
import { useCategoryCommissionCampaignJoin } from '@/views/CategoryCommissionCampaigns/services';
import useCategoryCommissionCampaignsJoinByPromoCode from '@/views/CategoryCommissionCampaigns/services/joinByPromoCode';
import ModalJoinCampaignResultError from './ModalJoinCampaignResultError';

const emit = defineEmits(['joinSuccess', 'joinDismiss', 'joinClose']);
const store = useStore();

const { isLoading: isJoining, join } = useCategoryCommissionCampaignJoin();
const {
  isSubmitting: isJoiningByPromoCode,
  joinCampaign: joinCampaignByPromoCode,
} = useCategoryCommissionCampaignsJoinByPromoCode();

const modalRef = ref(null);
const joinCampaignResultErrorModalRef = ref(null);
const campaign = ref(null);
const closeReason = ref(null);

const categoriesMapping = (categories) =>
  (categories ?? []).map((c) => c.name).join(', ');

const open = (apiDetail) => {
  campaign.value = apiDetail;
  closeReason.value = null;
  modalRef.value?.openModal?.();
};

const hideModal = () => {
  modalRef.value?.hiddenModal?.();
};

const onJoinDismissClick = () => {
  closeReason.value = 'dismiss';
  hideModal();
  emit('joinDismiss');
};

const onJoinCloseClick = () => {
  if (closeReason.value) {
    closeReason.value = null;
    return;
  }
  emit('joinClose');
};

const close = () => {
  closeReason.value = 'programmatic';
  hideModal();
};

const pushSuccessToast = () => {
  store.commit('common/SET_TOASTER_DATA', {
    type: 'success',
    title: '',
    description: 'Kampanyaya katıldınız.',
  });
};

const pushUnexpectedErrorToast = () => {
  store.commit('common/SET_TOASTER_DATA', {
    type: 'error',
    title: '',
    description:
      'İşlem sırasında beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
  });
};

const openJoinCampaignResultErrorModal = (errorPayload) => {
  joinCampaignResultErrorModalRef.value?.open?.({
    title: errorPayload?.title ?? '',
    message: errorPayload?.message ?? '',
  });
};

const handleJoinByCampaignId = async (campaignId) => {
  if (!campaignId) throw new Error('Campaign ID is required');

  const result = await join(campaignId);

  if (result.ok) {
    return {
      success: true,
      errorPayload: null,
      isUnexpectedError: false,
    };
  }

  const errorResponse = result?.error;
  const errorPayload = errorResponse?.data?.errors?.[0];
  const isUnexpectedError =
    Number(errorResponse?.status) >= 500 ||
    (!errorPayload?.title && !errorPayload?.message);

  return {
    success: false,
    errorPayload,
    isUnexpectedError,
  };
};

const handleJoinByPromoCode = async (promoCode) => {
  if (!promoCode) throw new Error('Promo code is required');

  const result = await joinCampaignByPromoCode(promoCode);

  if (result.success) {
    return {
      success: true,
      errorPayload: null,
      isUnexpectedError: false,
    };
  }

  const errorPayload = result?.error;
  const isUnexpectedError =
    Number(result?.error?.status) >= 500 ||
    (!errorPayload?.title && !errorPayload?.message);

  return {
    success: false,
    errorPayload,
    isUnexpectedError,
  };
};

const onJoinCampaign = async () => {
  const joinContext = campaign.value?.joinContext;
  const isPromoCodeFlow = joinContext?.type === 'promoCode';

  const joinResult = isPromoCodeFlow
    ? await handleJoinByPromoCode(joinContext?.value)
    : await handleJoinByCampaignId(joinContext?.value ?? campaign.value?.id);

  close();

  if (joinResult.success) {
    pushSuccessToast();
    emit('joinSuccess');
    return;
  }

  if (joinResult.isUnexpectedError) {
    pushUnexpectedErrorToast();
  } else {
    openJoinCampaignResultErrorModal(joinResult.errorPayload);
  }
};

defineExpose({
  open,
  close,
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

:deep(.modal-body) {
  padding: 0 16px 0 0 !important;
  margin-right: -16px !important;
}

:deep(.modal-footer) {
  padding: 0 !important;
}

.join-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  overflow-x: hidden;
  text-align: center;

  .join-modal-title {
    max-width: 560px;
    margin: 0;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.2px;
    line-height: 26px;
    text-align: center;
  }

  .join-modal-subtitle {
    width: 420px;
    margin: 0;
    color: $colorN80;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: -0.05px;
    line-height: 24px;
    opacity: 0.8;
  }

  .join-modal-card {
    display: flex;
    width: 100%;
    flex-direction: column;
    padding: 16px 12px;
    border-radius: 8px;
    margin-top: 8px;
    background: $colorM05;
    gap: 8px;
    text-align: left;

    .join-modal-card-title {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: -0.05px;
      line-height: 20px;
    }

    .join-modal-card-badge {
      width: fit-content;
      height: 24px;
      padding: 4px 8px;
      border: 1px solid #f4e;
      border-radius: 4px;
      background: #fff;
      color: #f4e;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
    }

    .join-modal-card-section {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .join-modal-card-section-item {
      display: block;

      .label {
        margin-right: 4px;
        color: #1c1c1e;
        font-size: 12px;
        font-weight: 400;
        letter-spacing: 0;
        line-height: 16px;
        vertical-align: middle;
        white-space: normal;
      }

      .value {
        display: inline;
        color: #1c1c1e;
        font-size: 12px;
        font-style: semi-bold;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 16px;
        overflow-wrap: anywhere;
        vertical-align: middle;
      }
    }
  }

  .join-modal-info {
    display: flex;
    width: 100%;
    align-items: flex-start;
    padding: 16px;
    border-radius: 8px;
    background: #e9f3ff;
    gap: 8px;
    text-align: left;

    .join-modal-info-text {
      font-size: 13px;
      font-weight: 400;
      letter-spacing: -0.05px;
      line-height: 18px;
    }
  }
}
</style>
